/**
 * Remember: always add new reducers to the /reducers/index.js file!
 * Also, add your initialState to './initialstate'!
 */
import isEqual from 'lodash/isEqual';
import * as types from '../actions/actiontypes'; //just externalizes action name constants
import initialState from './initialstate';
import {HttpStatusCodes, networkErrorCodes, objectNotFoundActions} from '../util/uiconstants';
import {currentResource} from '../util/utils';
import {DELETE_SAMPLE_REFERENCE, GET_OVERSIGHT_PROJECTS_BY_ENGAGEMENT, GET_OVERSIGHT_OTHER_PROJECTS_BY_ENGAGEMENT} from '../actions/actiontypes';
import Env from '../util/env';

/**
 * This reducer function handles all the actions related to the top-level objects.
 * @param {Object} state - A portion of the overall Redux state, specifically the
 *      store.<%=names.camel%> object, representing the current data.
 * @param {Object} action - Action object containing type and other values
 * @return {*} current state or array of language objects
 */
export default function errorReducer(state = initialState.errors, action) {
	switch (action.type) {
		case types.API_ERRORS: {
			//Hiding error toast message when user doesn't have oversight user created
			if (action?.action?.type === GET_OVERSIGHT_PROJECTS_BY_ENGAGEMENT || action?.action?.type === GET_OVERSIGHT_OTHER_PROJECTS_BY_ENGAGEMENT) {
				if (action?.action?.error?.response?.status === 403 || action?.action?.error?.response?.status === 106) {
					return state;
				}
			}

			if (networkErrorCodes.includes(action?.error?.code)) {
				return {
					message: currentResource.Errors[action?.error?.code.toUpperCase()],
					level: 'error'
				};
			}

			//Hiding error message notification when universal call fails, for nav.js purposes, and Archive
			if (action.action?.payload?.supressErrorOnUi || action?.supressErrorOnUi || action?.error?.response?.status === HttpStatusCodes.LengthRequired) {
				// don't show error on UI for e.g: we don't want to show error on UI if oversight and Iris api calls failed
				return state;
			}

			//If application gateway is rejecting input messages due to some suspicious special characters(forbidden by EY Information Security),
			if (action.error?.response?.status === 403 && action?.error?.response?.data?.errors === undefined) {
				return {
					message: currentResource.Errors[200001],
					level: 'error'
				};
			}

			//Hiding error messages on UI for object(s) not found errors
			if (objectNotFoundActions.includes(action?.action?.type)) {
				if (action?.error?.response?.status === HttpStatusCodes.NotFound) {
					state = {
						...state,
						isObjectNotFoundError: true
					};
					return state;
				}
			}

			if (action?.action?.type === DELETE_SAMPLE_REFERENCE && action?.action?.customErrorMessage) {
				return {
					message: action.action.customErrorMessage,
					level: 'error'
				};
			}

			/* Notice that this is simply returning the action payload, <%=names.camel%>.
			 * This is because the combineReducers() call wraps this reducer
			 * and passes it only the portion of the store state that it
			 * is concerned with. */
			console.warn('errorReducer -> ', action?.error?.requestUrl);
			// Hiding error message notification when universal call fails, for nav.js purposes, and Archive
			if (action.error && action.error.requestUrl) {
				// Handle share activity api errors
				if (action?.error.requestUrl.indexOf('/LinkedForms') > 0 && action.error?.config?.method === 'post') {
					if (action?.error?.response?.data?.errors?.Error) {
						return action.error.response.data.errors.Error[0];
					}
					return action?.error?.response?.data?.errors;
				}

				if (
					action.error.requestUrl.indexOf('HelixProjects') > 0 &&
					action.error.requestUrl.indexOf('Engagements') > 0 &&
					action.error.requestUrl.indexOf('Imports') > 0 &&
					action.error?.config?.method === 'delete'
				) {
					return {
						message: currentResource.Errors[34040],
						level: 'error'
					};
				}

				// Handle doc service API error messages
				if (action.error.requestUrl?.startsWith(Env.getDocServiceUrl()) || action.error.response?.request?.responseURL?.startsWith(Env.getDocServiceUrl())) {
					return handleDocServiceErrors(action) || state;
				}
				if (action.error.requestUrl.indexOf('engagementmetadata.json') > 0) {
					return state;
				}

				if (action.error.requestUrl.indexOf('workspaces.json') > 0) {
					return {
						message: currentResource.Errors[7014],
						level: 'error'
					};
				}

				if (action.error.requestUrl.indexOf('Forms') > 0 && action.error.requestUrl.indexOf('Engagements') > 0 && action.error?.config?.method === 'post') {
					if (action.error.response.data.errors && action.error.response.data.errors) {
						let code = 0;

						if (action.error.response.data.errors.EngagementProfileRequirement) {
							code = Number(action.error.response.data.errors.EngagementProfileRequirement[0].split(' - ')[0]);
						} else {
							code = Number(action.error.response.data.errors?.Error[0].split(' - ')[0]);
						}

						return {
							message: currentResource.Errors[code],
							level: 'error'
						};
					}

					// if this 412 - content update is in progress
					// need to ask user to wait
					if (action.error.response?.status === 412) {
						return {
							message: currentResource.Errors[4025],
							level: 'error'
						};
					}

					return {
						message: currentResource.Errors[4024],
						level: 'error'
					};
				}

				if (action.error.requestUrl.indexOf('/Archives') > 0) {
					if (action.error.response.data.Errors) {
						action.error.response.data.errors = action.error.response.data.Errors;
						action.error.response.data.Errors = null;
					}
				}
			}
			if (action.error.request?.responseURL) {
				// Handle doc service API error messages
				if (action.error.requestUrl?.startsWith(Env.getDocServiceUrl()) || action.error.request?.responseURL?.startsWith(Env.getDocServiceUrl())) {
					return handleDocServiceErrors(action) || state;
				}
			}

			if (action?.error?.response) {
				if (action.error.response?.data?.Errors) {
					return action.error.response.data.Errors;
				} else if (action.error.response?.data?.errors) {
					if (action.error.response.data.errors.Error) {
						if (action?.action?.customErrorMessage) {
							// Extract error code from the API response
							const errorCodeWithMessage = action.error.response.data.errors.Error[0];
							const errorCode = errorCodeWithMessage.split(' - ')[0];
							const ErrorMessage = currentResource.Errors[errorCode];
							return {
								message: `${errorCode}: ${ErrorMessage ?? action.action.customErrorMessage}`,
								level: 'error'
							};
						}
						return action.error.response.data.errors.Error[0];
					} else if (Object.keys(action.error.response.data.errors).length > 0) {
						let list = [];
						Object.entries(action.error.response.data.errors).forEach((error) => {
							let code = error[1][0].split(' - ')[0];
							let message = error[1][0].split(' - ')[1];

							if (!list.some((item) => item.ErrorCode === code)) {
								list.push({
									ErrorCode: code,
									Message: message
								});
							}
						});
						if (action?.action?.customErrorMessage) {
							// Extract error code from the API response
							const errorCode = list[0]?.ErrorCode;
							return {
								message: `${errorCode}: ${action.action.customErrorMessage}`,
								level: 'error'
							};
						}

						return isEqual(list, state) ? state : list;
					}
				}

				if (action.error.response?.data?.Message) {
					return action.error.response.data.Message;
				} else {
					return action.error?.response?.statusText;
				}
			}
			if (action.error === 'downloadCancelled') {
				return handleDocServiceErrors(action) || state;
			} else {
				return action?.error;
			}
		}
		case types.CLIENT_VALIDATION_ERROR:
			return {
				message: action.message,
				level: 'error'
			};
		case types.SUCCESS_MESSAGE:
			return {
				message: action.message,
				level: 'success',
				autoDismissInSeconds: action.autoDismissInSeconds
			};
		case types.WARNING_MESSAGE:
			return {
				message: action.message,
				level: 'warning'
			};
		case types.INFO_MESSAGE:
			return {
				message: action.message,
				level: 'info'
			};
		case types.CLEAR_ERRORS:
			return {};
		case 'DOCHELPER_ERROR':
		case 'KRONOS_DOCHELPER_ERROR':
		case 'KRONOS_UPCHUNK_UPLOAD_ERROR': {
			switch (action.error[0]) {
				case HttpStatusCodes.PreConditionFailed:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.PreConditionFailed]};
				case HttpStatusCodes.RequestEntityTooLarge:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.RequestEntityTooLarge]};
				case HttpStatusCodes.Unauthorized:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.Unauthorized]};
				case HttpStatusCodes.LengthRequired:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.LengthRequired]};
				case HttpStatusCodes.Forbidden:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.Forbidden]};
				case HttpStatusCodes.BadRequest:
					return {message: currentResource.DocumentHelper[HttpStatusCodes.BadRequest]};
				default:
					return {
						message: `[DocHelper Error][${action.error[1]}]: ${currentResource.Errors[Number(action.error[0])] || action.error[2]}`
					};
			}
		}
		case 'INVALID_RELATED_ENTITY': {
			const errorObject = action.error;
			const entityError = '<strong>' + errorObject[0] + ':' + '</strong>';
			return {
				message: `${entityError} ${currentResource.Errors[errorObject[0]]}`
			};
		}

		default:
			return state;
	}
}

function handleDocServiceErrors(action) {
	const defaultErrorCode = 600;
	if (action.error.response && action.error.response.data) {
		const docServiceErrorMessage = action.error.response.data.ErrorMessage?.toLowerCase();
		const docServiceErrorCode = action.error.response.data.ErrorCode;

		if (docServiceErrorCode) {
			if (action.error.response.status === HttpStatusCodes.Conflict) {
				return createErrorItem(null, 'error', currentResource.DocumentHelper[4099]);
			} else if (action.error.response.status === HttpStatusCodes.RequestUriTooLong) {
				let message = currentResource.DocumentHelper[HttpStatusCodes.RequestUriTooLong];
				let maxFileNameLength = action?.error?.documentSettingConfiguration?.maxAllowedFileNameLength;
				if (!Number.isFinite(maxFileNameLength)) {
					maxFileNameLength = 120;
				}
				message = message.replace('{0}', maxFileNameLength);

				return createErrorItem(null, 'error', message);
			} else if (action.error.response.status === HttpStatusCodes.RequestEntityTooLarge) {
				return createErrorItem(null, 'error', currentResource.DocumentHelper[HttpStatusCodes.RequestEntityTooLarge]);
			}
			if (Array.isArray(docServiceErrorCode)) {
				const firstCode = docServiceErrorCode[0];
				const resolvedMessage = currentResource.Errors[firstCode] || action.error.response.data.ErrorMessage || firstCode;
				return createErrorItem(null, 'error', resolvedMessage);
			}
			return createErrorItem(docServiceErrorCode);
		}
		if (docServiceErrorMessage) {
			if (docServiceErrorMessage.indexOf('document with the same name exists') !== -1) {
				return createErrorItem(601);
			}
			if (docServiceErrorMessage.indexOf('document is already in collaboration.') !== -1) {
				return createErrorItem(602);
			}
			if (docServiceErrorMessage.indexOf('document not found') !== -1) {
				return createErrorItem(603);
			}
			return createErrorItem(defaultErrorCode);
		}
	}
	if (action.error === 'downloadCancelled') {
		return createErrorItem(1250);
	}
}

function createErrorItem(errorCode, errorLevel, message) {
	return {
		message: message || currentResource.Errors[errorCode],
		level: errorLevel || 'error'
	};
}
